import { mostrarAcesso } from "@/components/AccessShare";
import React, { useEffect, useState, useRef } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Plus, Download, Search, Loader2, Trash2, Link2, CheckCircle2, Upload, FileSpreadsheet, GraduationCap, Pencil,
  MoreVertical, CheckCircle, PauseCircle, Ban, Eye, UserCog, KeyRound,
} from "lucide-react";
import { EditPersonDialog } from "@/components/EditPersonDialog";
import { NewUserDialog } from "@/components/NewUserDialog";
import { EnrollmentsDialog } from "@/components/EnrollmentsDialog";
import { Pager } from "@/components/Pager";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { impersonate } from "@/components/ImpersonationBanner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";

import { confirmar } from "@/components/ConfirmDialog";
export default function Students() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [students, setStudents] = useState(null);
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("busca") || "");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [groups, setGroups] = useState([]);
  const [impProf, setImpProf] = useState("");
  const [impGroup, setImpGroup] = useState("");
  const [impCourse, setImpCourse] = useState("");
  const [courses, setCourses] = useState([]);
  const fileRef = useRef(null);
  const [enrollStudent, setEnrollStudent] = useState(null);
  const [editing, setEditing] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null); // { student, status }
  const [statusForm, setStatusForm] = useState({ until: "", reason: "" });
  const [savingStatus, setSavingStatus] = useState(false);

  const openStatus = (student, status) => {
    if (status === "ativo") return applyStatus(student, "ativo", {});
    setStatusForm({ until: "", reason: "" });
    setStatusDialog({ student, status });
  };
  const applyStatus = async (student, status, extra) => {
    setSavingStatus(true);
    try {
      await api.put(`/students/${student.id}/status`, { status, ...extra });
      toast.success({ ativo: "Aluno ativado.", suspenso: "Aluno suspenso.", bloqueado: "Aluno bloqueado." }[status]);
      setStatusDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingStatus(false); }
  };
  const PAGE = 50;
  const [page, setPage] = useState(1);
  const listTopRef = useRef(null);
  const goPage = (p) => { setPage(p); listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  useEffect(() => { setPage(1); }, [q, filter]);

  const openEnroll = (s) => setEnrollStudent(s);

  const load = () => api.get("/students").then((r) => setStudents(r.data)).catch(() => setStudents([]));
  useEffect(() => { load(); }, []);

  const doImport = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (impGroup) fd.append("group_id", impGroup);
      if (impCourse) fd.append("course_id", impCourse);
      const res = await api.post("/students/import", fd);
      setImportResult(res.data);
      load();
      toast.success(`Importação concluída: ${res.data.created} novos, ${res.data.updated} atualizados.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const accessMeta = (s) => {
    const st = s.access_status || "ativo";
    if (st === "desativado") return { label: "Desativado", cls: "bg-slate-200 text-slate-700" };
    if (st === "bloqueado") return { label: "Bloqueado", cls: "bg-red-50 text-red-700" };
    if (st === "suspenso") {
      const until = s.suspended_until;
      if (until && until < new Date().toISOString().slice(0, 10)) return { label: "Ativo", cls: "bg-green-50 text-green-700" };
      return { label: until ? `Suspenso até ${until.split("-").reverse().join("/")}` : "Suspenso", cls: "bg-amber-50 text-amber-700" };
    }
    return { label: "Ativo", cls: "bg-green-50 text-green-700" };
  };

  const statusMeta = (s) => {
    if (s === "cadastrado") return { label: "Cadastrado", cls: "bg-teal-50 text-teal-700" };
    if (s === "importado") return { label: "Importado", cls: "bg-sky-50 text-sky-700" };
    if (s === "primeiro_acesso") return { label: "Aguardando 1º acesso", cls: "bg-amber-50 text-amber-700" };
    return { label: "Pendente", cls: "bg-orange-50 text-orange-700" };
  };


  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copiado!");
  };

  const resend = async (s) => {
    if (!(await confirmar(`Gerar um novo código de 1º acesso para ${s.name} e enviar por e-mail? O código anterior deixa de valer.`))) return;
    try {
      const res = await api.post(`/students/${s.id}/resend-invite`);
      mostrarAcesso(res.data);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (s) => {
    if (!(await confirmar(`Excluir ${s.name}? O cadastro e o acesso dele serão apagados (não dá para desfazer).`))) return;
    await api.delete(`/students/${s.id}`);
    load();
    toast.success("Aluno removido.");
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/students/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "alunos_iep.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Arquivo exportado!");
    } catch (e) { toast.error("Falha ao exportar."); }
    finally { setExporting(false); }
  };

  const filtered = (students || []).filter((s) => {
    // celular: compara só os números (com ou sem DDD, parênteses, traço ou +55)
    let qd = q.replace(/\D/g, "");
    if (qd.length > 11 && qd.startsWith("55")) qd = qd.slice(2);
    const matchPhone = qd.length >= 4 && (s.phone || "").replace(/\D/g, "").replace(/^55(?=\d{10,11}$)/, "").includes(qd);
    const matchQ = !q || s.name?.toLowerCase().includes(q.toLowerCase()) || s.email?.toLowerCase().includes(q.toLowerCase()) || (s.reg_code || "").includes(q.trim()) || matchPhone;
    const access = s.access_status || "ativo";
    const matchF = filter === "all" || (["suspenso", "bloqueado", "desativado"].includes(filter) ? access === filter : s.status === filter);
    return matchQ && matchF;
  });

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Alunos</h1>
          <p className="text-slate-500 mt-1">Cadastre usuários, importe a base e gerencie matrículas.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="btn-import-students" variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); api.get("/groups").then((r) => setGroups(r.data)).catch(() => setGroups([])); api.get("/courses").then((r) => setCourses(r.data)).catch(() => {}); }}>
            <Upload className="h-4 w-4 mr-1.5" /> Importar
          </Button>
          <Button data-testid="btn-export-students-excel" variant="outline" onClick={exportExcel} disabled={exporting}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" /> Exportar Excel</>}
          </Button>
          <Button data-testid="btn-new-user" onClick={() => setOpen(true)} className="text-white font-semibold hover:opacity-90" style={{ background: "#1E9E2A" }}>
            <Plus className="h-4 w-4 mr-1.5" /> Novo aluno
          </Button>
        </div>
      </div>

      {/* filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="table-student-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, e-mail, celular ou ID..." className="pl-9 h-10" />
        </div>
        <div className="flex flex-wrap gap-1 bg-slate-100 rounded-lg p-1">
          {[["all", "Todos"], ["cadastrado", "Cadastrados"], ["primeiro_acesso", "Aguardando 1º acesso"], ["pendente", "Pendentes"], ["importado", "Importados"], ["suspenso", "Suspensos"], ["bloqueado", "Bloqueados"], ["desativado", "Desativados"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === v ? "bg-white text-[#232D4F] shadow-sm" : "text-slate-500"}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div ref={listTopRef} className="scroll-mt-24">
        {students && filtered.length > 0 && <Pager compact page={page} perPage={PAGE} total={filtered.length} onChange={goPage} />}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {students === null ? (
          <div className="p-4 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">Nenhum aluno encontrado.</div>
        ) : (
          <div className="overflow-x-auto iep-scroll">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/60">
                  <TableHead>Aluno</TableHead>
                  <TableHead className="hidden xl:table-cell">ID</TableHead>
                  <TableHead className="hidden md:table-cell">Documento</TableHead>
                  <TableHead className="hidden lg:table-cell">Nascimento</TableHead>
                  <TableHead className="hidden lg:table-cell">Cadastrado em</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead>Acesso</TableHead>
                  <TableHead className="text-right">Opções</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.slice((page - 1) * PAGE, page * PAGE).map((s) => (
                  <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                    <TableCell className="min-w-[220px]">
                      <button onClick={() => navigate(`/admin/alunos/${s.id}`)} className="group flex items-center gap-3 text-left" data-testid={`open-student-${s.id}`}>
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#1E9E2A] to-[#147a1f] text-sm font-bold text-white">{(s.name || "?").trim()[0]?.toUpperCase()}</span>
                        <span className="min-w-0">
                          <span className="block font-semibold text-slate-800 group-hover:text-[#1E9E2A]">{s.name}</span>
                          <span className="block text-xs text-slate-500 truncate max-w-[260px]">{s.email}</span>
                        </span>
                      </button>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell font-mono text-xs text-slate-500 whitespace-nowrap">{s.reg_code || "—"}</TableCell>
                    <TableCell className="hidden md:table-cell text-slate-600 whitespace-nowrap">{s.document || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-600 whitespace-nowrap">{/^\d{4}-\d{2}-\d{2}/.test(s.birth_date || "") ? s.birth_date.slice(0, 10).split("-").reverse().join("/") : (s.birth_date || "—")}</TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-600 whitespace-nowrap">{(s.original_registered_at || s.registered_at || s.created_at || "").slice(0, 10).split("-").reverse().join("/") || "—"}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${statusMeta(s.status).cls}`}>
                        {statusMeta(s.status).label}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${accessMeta(s).cls}`}>{accessMeta(s).label}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" data-testid={`student-options-${s.id}`} title="Opções">
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onSelect={() => navigate(`/admin/alunos/${s.id}`)}><UserCog className="h-4 w-4 mr-2" /> Abrir perfil completo</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => navigate(`/admin/alunos/${s.id}#seguranca`)}><KeyRound className="h-4 w-4 mr-2" /> Definir senha</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => setEditing(s)}><Pencil className="h-4 w-4 mr-2" /> Edição rápida (dados e senha)</DropdownMenuItem>
                          <DropdownMenuItem disabled={s.status !== "cadastrado"} onSelect={() => impersonate("aluno", s.id, setUser, navigate)} className="text-amber-700" data-testid={`impersonate-${s.id}`}>
                            <Eye className="h-4 w-4 mr-2" /> Acessar como aluno
                            {s.status !== "cadastrado" && <span className="ml-auto text-[10px] text-slate-400">após ativação</span>}
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => openEnroll(s)}><GraduationCap className="h-4 w-4 mr-2" /> Matrículas</DropdownMenuItem>
                          {s.status !== "cadastrado" && (
                            <DropdownMenuItem onSelect={() => resend(s)}><Link2 className="h-4 w-4 mr-2" /> Enviar acesso (novo código)</DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {(s.access_status || "ativo") !== "ativo" && (
                            <DropdownMenuItem onSelect={() => openStatus(s, "ativo")} className="text-green-700"><CheckCircle className="h-4 w-4 mr-2" /> Ativar</DropdownMenuItem>
                          )}
                          <DropdownMenuItem onSelect={() => openStatus(s, "suspenso")} className="text-amber-700" data-testid={`suspend-student-${s.id}`}><PauseCircle className="h-4 w-4 mr-2" /> Suspender</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => openStatus(s, "bloqueado")} className="text-red-600"><Ban className="h-4 w-4 mr-2" /> Bloquear</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => remove(s)} className="text-red-600" data-testid={`delete-student-${s.id}`}><Trash2 className="h-4 w-4 mr-2" /> Excluir</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager page={page} perPage={PAGE} total={filtered.length} onChange={goPage} testid="students-pager" />
          </div>
        )}
      </div>

      <EditPersonDialog person={editing} kind="student" onClose={() => setEditing(null)} onSaved={load} />
      <NewUserDialog open={open} onOpenChange={setOpen} onCreated={load} defaultRole="aluno" />

      <Dialog open={!!statusDialog} onOpenChange={(o) => !o && setStatusDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{statusDialog?.status === "suspenso" ? "Suspender aluno" : "Bloquear aluno"}</DialogTitle>
            <DialogDescription>
              {statusDialog?.student?.name} — {statusDialog?.status === "suspenso"
                ? "o acesso fica pausado. Com data final, volta sozinho no dia seguinte."
                : "o acesso fica bloqueado até você ativar de novo."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {statusDialog?.status === "suspenso" && (
              <div className="space-y-1.5">
                <Label>Suspenso até (opcional)</Label>
                <Input type="date" value={statusForm.until} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setStatusForm((p) => ({ ...p, until: e.target.value }))} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Motivo (uso interno, opcional)</Label>
              <Input value={statusForm.reason} onChange={(e) => setStatusForm((p) => ({ ...p, reason: e.target.value }))} placeholder="Ex.: pagamento pendente" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)}>Cancelar</Button>
            <Button disabled={savingStatus} onClick={() => applyStatus(statusDialog.student, statusDialog.status, statusForm)}
              className="text-white" style={{ background: statusDialog?.status === "suspenso" ? "#B45309" : "#DC2626" }} data-testid="confirm-status">
              {savingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : statusDialog?.status === "suspenso" ? "Suspender" : "Bloquear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Importar alunos</DialogTitle>
            <DialogDescription>
              Envie um arquivo CSV ou Excel (.xlsx) exportado da plataforma antiga.
            </DialogDescription>
          </DialogHeader>

          {importResult ? (
            <div className="space-y-4 py-2">
              <div className="rounded-lg bg-teal-50 border border-teal-100 p-4 text-center">
                <CheckCircle2 className="h-8 w-8 text-teal-600 mx-auto mb-2" />
                <p className="text-sm text-slate-700 font-medium">Importação concluída!</p>
                <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                  <div><div className="font-display text-xl font-bold text-teal-700">{importResult.created}</div><div className="text-[11px] text-slate-500">Novos</div></div>
                  <div><div className="font-display text-xl font-bold text-sky-700">{importResult.updated}</div><div className="text-[11px] text-slate-500">Atualizados</div></div>
                  <div><div className="font-display text-xl font-bold text-slate-400">{importResult.skipped}</div><div className="text-[11px] text-slate-500">Ignorados</div></div>
                </div>
                {importResult.enrolled > 0 && <div className="text-xs text-slate-600 mt-3 text-left">
                  <strong>{importResult.enrolled} matrícula(s):</strong>{Object.entries(importResult.by_course || {}).map(([k, v]) => <div key={k}>• {k}: {v}</div>)}</div>}
                {Object.keys(importResult.by_group || {}).length > 0 && <div className="text-xs text-slate-600 mt-2 text-left">
                  <strong>Turmas:</strong>{Object.entries(importResult.by_group).map(([k, v]) => <div key={k}>• {k}: {v}</div>)}</div>}
                {importResult.unknown_courses?.length > 0 && <p className="text-xs text-amber-700 mt-2 text-left">Cursos não encontrados (não matriculados): {importResult.unknown_courses.join(", ")}</p>}
                {importResult.staff_skipped > 0 && (
                  <p className="text-[11px] text-slate-500 mt-3">{importResult.staff_skipped} cadastro(s) de admin/professor na planilha não viraram aluno.</p>
                )}
                <p className="text-[11px] text-slate-500 mt-2">Nenhum e-mail foi enviado. Use o botão de convite de cada aluno quando quiser liberar o acesso.</p>
              </div>
              <Button className="w-full" variant="outline" onClick={() => setImportResult(null)}>Importar outro arquivo</Button>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-xs text-slate-500 leading-relaxed">
                Colunas reconhecidas: <strong>Nome</strong>, <strong>E-mail</strong> (obrigatório), CPF/Documento,
                Data de Nascimento, Telefone, CEP, Rua, Número, Complemento, Bairro, Cidade, Estado.
                Os dados são padronizados como na tela de cadastro (CPF, data, telefone, CEP e UF).
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Professora</Label>
                  <select value={impProf} onChange={(e) => { setImpProf(e.target.value); setImpGroup(""); }} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="import-prof">
                    <option value="">Todas</option>
                    {[...new Set(groups.map((g) => (g.instructor_name || "").replace(" (acesso pendente)", "")).filter(Boolean))].sort().map((n) => <option key={n} value={n}>{n}</option>)}
                  </select></div>
                <div className="space-y-1.5"><Label>Turma</Label>
                  <select value={impGroup} onChange={(e) => setImpGroup(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="import-group">
                    <option value="">— Sem turma —</option>
                    {groups.filter((g) => !impProf || (g.instructor_name || "").replace(" (acesso pendente)", "") === impProf).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select></div>
              </div>
              <div className="space-y-1.5"><Label>Matricular no curso</Label>
                <select value={impCourse} onChange={(e) => setImpCourse(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="import-course">
                  <option value="">— Não matricular (ou usar a coluna Curso da planilha) —</option>
                  {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select></div>
              <p className="text-xs text-slate-500 -mt-2">Relatório de matrículas da plataforma antiga: o curso, as datas (início e vencimento) e a turma da professora (coluna Grupo) são lidos da própria planilha. Alunos sem grupo vão para a turma escolhida acima, se não estiverem em nenhuma.</p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls,.txt"
                data-testid="import-file-input"
                onChange={(e) => doImport(e.target.files)}
                className="hidden"
              />
              <button
                data-testid="import-dropzone"
                onClick={() => fileRef.current?.click()}
                disabled={importing}
                className="w-full rounded-xl border-2 border-dashed border-slate-200 hover:border-[#232D4F] hover:bg-slate-50 p-8 text-center transition-colors"
              >
                {importing ? (
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-[#232D4F]" />
                ) : (
                  <FileSpreadsheet className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                )}
                <div className="text-sm font-medium text-slate-700 mt-1">
                  {importing ? "Importando..." : "Clique para selecionar o arquivo"}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">CSV, XLSX ou XLS</div>
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Enroll dialog */}
      <EnrollmentsDialog student={enrollStudent} onClose={() => setEnrollStudent(null)} />
    </div>
  );
}
